/*
  A save that did not go through, and the way to try it again.

  **Built 10 September 2026 for job 25**, and it is the other half of the
  owner's own answer about losing the connection. He took the recommendation on
  5 September: **count entry keeps working with no signal; every other screen
  just says so loudly and lets the person press it again.**

  The first half is `outbox.js`. This is the second, and it is deliberately much
  smaller. Eleven of the thirteen screens in OnHand are used at a desk on a good
  connection: building a holding queue for them is work spent on a problem they
  do not have, and a person sitting at a desk can press a button.

  **Why it is not simply a pop-up message.** OnHand already reports a failed save
  in a message that goes on its own after five seconds. That is right for
  something you only need to be told. It is wrong for something you have to DO,
  because the thing you have to do vanishes while you are reading it. The bar
  stays until the save works or the person gives up, and it carries the button.

  **It holds one at a time on purpose.** A dropped connection fails whatever the
  person tries next as well, and a stack of five identical bars saying the
  connection dropped is noise on top of a problem. The newest one wins.
*/

let current = null;
let listeners = [];

function tell() {
  listeners.forEach(fn => { try { fn(current); } catch { /* one bad listener does not stop the rest */ } });
}

export function subscribe(fn) {
  listeners.push(fn);
  fn(current);
  return () => { listeners = listeners.filter(l => l !== fn); };
}

/*
  Say that a save did not go through.

  `retry` is what to run when the person presses the button, and it is the whole
  point: a bar with nothing behind the button is worse than no bar, because it
  tells somebody there is something they can do and then does not do it.
*/
export function saveFailed(retry, what) {
  current = { retry, what: what || null, at: Date.now() };
  tell();
}

export function clearSaveFailed() {
  if (!current) return;
  current = null;
  tell();
}

export function currentFailure() {
  return current;
}
